import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores/user";
import { bbsService } from "@/services/bbs/bbs";

// 菜单项类型
interface NavMenuItem {
  key?: string;
  label: string;
  path?: string | null;
  desc?: string;
  type?: string;
  iconStyle?: "compensation" | "pay";
  children?: NavMenuItem[];
}

const SERVICE_GUIDE_CATEGORY_ID = 22;
const NOTICE_CENTER_CATEGORY_ID = 25;
const NAV_MENU_CACHE_VERSION = 1;

export function useNavMenu() {
  const { t, locale } = useI18n();
  const userStore = useUserStore();
  const isLoggedIn = computed(() => !!userStore.userInfo);

  // PC 端菜单状态
  const activeMenuPc = ref<string | null>(null);
  const activeSecond = ref<string | null>(null);
  const isInDropdown = ref(false);
  let leaveTimer: ReturnType<typeof setTimeout> | null = null;

  // 移动端菜单状态
  const isMobileMenuOpen = ref(false);
  const menuLevel = ref(1);
  const activeMenu = ref<string | null>(null);

  // 写死的菜单部分
  const staticMenus = computed<NavMenuItem[]>(() => {
    const isChineseLanguage = String(locale.value)
      .toLowerCase()
      .startsWith("zh");
    const china3plChildren: NavMenuItem[] = [
      { label: t("menu.china_inventory"), type: "title" },
      {
        label: t("menu.buy_cn_inventory"),
        path: "/delivery-precautions?type=buy&orderType=8&requestGroup=3&title=menu.buy_cn_inventory",
      },
      {
        label: t("menu.china_inventory_upload"),
        path: "/delivery-precautions?type=delivery&orderType=9&requestGroup=3&title=menu.china_inventory_upload",
      },
    ];

    const menus: NavMenuItem[] = [
      {
        key: "purchase",
        label: t("outbound.电商快递"),
        children: [
          {
            key: "deliveryguide",
            label: t("menu.ecommerce_order"),
            children: [
              { label: t("menu.buying_service"), type: "title" },
              {
                label: t("menu.buying_service_order"),
                path: "/delivery-precautions?type=buy&orderType=1&requestGroup=1&title=menu.buying_service_order",
              },
              {
                label: t("menu.buying_service_excel"),
                path: "/bulk-excel?orderType=1",
              },
              { label: t("menu.delivery_service"), type: "title" },
              {
                label: t("menu.delivery_service_order"),
                path: "/delivery-precautions?type=delivery&orderType=2&requestGroup=1&title=menu.delivery_service_order",
              },
              {
                label: t("menu.delivery_service_excel"),
                path: "/bulk-excel?orderType=2",
              },
            ],
          },
          // {
          //   key: "ocean_logistics",
          //   label: t("menu.ocean_logistics_order"),
          //   children: [
          //     { label: t("menu.buying_service"), type: "title" },
          //     {
          //       label: t("menu.buying_service_order"),
          //       path: "/delivery-precautions?type=buy&orderType=3&requestGroup=1&title=menu.buying_service_order",
          //     },
          //     { label: t("menu.delivery_service"), type: "title" },
          //     {
          //       label: t("menu.delivery_service_order"),
          //       path: "/delivery-precautions?type=delivery&orderType=4&requestGroup=1&title=menu.delivery_service_order",
          //     },
          //   ],
          // },
        ],
      },
      {
        key: "lcl",
        label: "LCL/FCL",
        children: [
          {
            key: "ocean_logistics",
            label: t("menu.ocean_logistics_order"),
            children: [
              { label: t("menu.buying_service"), type: "title" },
              {
                label: t("menu.buying_service_order"),
                path: "/delivery-precautions?type=buy&orderType=3&requestGroup=1&title=menu.buying_service_order",
              },
              { label: t("menu.delivery_service"), type: "title" },
              {
                label: t("menu.delivery_service_order"),
                path: "/delivery-precautions?type=delivery&orderType=4&requestGroup=1&title=menu.delivery_service_order",
              },
            ],
          },
        ],
      },
      {
        key: "rockets",
        label: t("menu.rocket_service"),
        children: [
          {
            key: "rocket_cn",
            label: t("menu.rocket_china"),
            children: [
              { label: t("menu.buying_service"), type: "title" },
              {
                label: t("menu.buying_service_order"),
                path: "/delivery-precautions?type=buy&orderType=5&requestGroup=2&title=menu.buying_service_order",
              },
              { label: t("menu.delivery_service"), type: "title" },
              {
                label: t("menu.delivery_service_order"),
                path: "/delivery-precautions?type=delivery&orderType=6&requestGroup=2&title=menu.delivery_service_order",
              },
            ],
          },
          {
            key: "rocket_kr",
            label: t("menu.rocket_korea"),
            children: [
              { label: t("menu.delivery_service"), type: "title" },
              {
                label: t("menu.delivery_service_order"),
                path: "/delivery-precautions?type=delivery&orderType=7&requestGroup=2&title=menu.delivery_service_order",
              },
            ],
          },
        ],
      },
      {
        key: "3PL",
        label: "3PL",
        children: [
          {
            key: "3pl_cn",
            label: t("menu.cn_3pl_inbound"),
            desc: t("menu.cn_3pl_inbound_desc"),
            children: china3plChildren,
          },
          {
            key: "3pl_kr",
            label: t("menu.kr_3pl_inbound"),
            desc: t("menu.kr_3pl_inbound_desc"),
            children: [
              {
                label: t("menu.buy_cn"),
                type: "title",
              },

              {
                label: isChineseLanguage
                  ? t("menu.korea_inventory_buy_order")
                  : t("menu.buy_cn_inventory"),
                path: "/delivery-precautions?type=buy&orderType=11&requestGroup=3&title=menu.buy_cn_delivery",
              },
              {
                label: isChineseLanguage
                  ? t("menu.korea_inventory_transfer_order")
                  : t("menu.china_inventory_upload"),
                path: "/delivery-precautions?type=delivery&orderType=10&requestGroup=3&title=menu.buy_cn_delivery",
              },
              {
                label: t("menu.buy_kr"),
                type: "title",
              },
              {
                label: t("menu.buy_kr_delivery"),
                path: "/delivery-precautions?type=buy&orderType=12&requestGroup=3&title=menu.buy_kr_delivery",
              },
            ],
          },
          {
            key: "3pl_kr_outbound",
            label: t("menu.kr_3pl_outbound"),
            desc: t("menu.kr_3pl_outbound_desc"),
            children: [
              { label: t("menu.inventory_dropshipping_kr"), type: "title" },
              {
                label: t("menu.inventory_dropshipping_kr_order"),
                path: "/delivery-precautions?type=buy&orderType=13&requestGroup=3&title=menu.inventory_dropshipping_kr_order",
              },
              {
                label: t("menu.inventory_dropshipping_kr_excel"),
                path: "/bulk-excel?orderType=13",
              },
            ],
          },
        ],
      },
    ];
    // 登录后才显示"我的页面"菜单
    if (isLoggedIn.value) {
      menus.push({
        key: "mypage",
        label: t("menu.my_page"),
        children: [
          {
            key: "order",
            label: t("menu.order_main"),
            children: [
              { label: t("button.all_order"), path: "/orders" },
              { label: t("menu.product_main"), path: "/product-manage" },
              { label: t("icon.refresh"), path: "/stock-manage" },
            ],
          },
          {
            key: "pay",
            label: t("menu.payment_management"),
            children: [
              {
                label: t("tab_title.payment settlement"),
                path: "/pay",
                iconStyle: "pay" as const,
              },
              { label: t("tab_title.paymentL"), path: "/pay?tab=paymentL" },
              { label: t("tab_title.deposit_req"), path: "/deposit-ko" },
              ...(userStore.userInfo?.showCompensationMenu === 1
                ? [
                    {
                      label: t("tab_title.compensation_deposit_management"),
                      path: "/compensation-deposit",
                      iconStyle: "compensation" as const,
                    },
                  ]
                : []),
            ],
          },
          {
            key: "acount",
            label: t("menu.account_management"),
            children: [
              { label: t("menu.member_info"), path: "/my-page" },
              { label: t("menu.coupon_list"), path: "/coupons" },
              { label: t("menu.point_management"), path: "/point" },
              { label: t("menu.address_management"), path: "/receive-address" },
              { label: t("menu.cooperation_management"), path: "/cooperation" },
            ],
          },
        ],
      });
    }
    return menus;
  });

  const isNoDataMenu = (item: any) => {
    return item.parentId === 41;
  };

  const getDynamicMenuLabel = (item: any) => {
    const isKorean = String(locale.value).toLowerCase().startsWith("ko");
    return isKorean
      ? item.nameKr || item.nameCn || item.name || ""
      : item.nameCn || item.nameKr || item.name || "";
  };

  const getDynamicMenuPath = (item: any, sectionRootId?: number | null) => {
    if (Number(item.id) === NOTICE_CENTER_CATEGORY_ID) {
      const firstPublicChild = (item.children || []).find(
        (child: any) => Number(child.onlyLoginIn) !== 1,
      );
      const categoryId = firstPublicChild?.id || item.id;
      return `/notice-center?categoryId=${categoryId}&page=1&search=`;
    }
    if (sectionRootId === NOTICE_CENTER_CATEGORY_ID) {
      return `/notice-center?categoryId=${item.id}&page=1&search=`;
    }
    if (item.level !== 3) return null;
    const label = getDynamicMenuLabel(item);
    if (isNoDataMenu(item))
      return (
        "/nodata?categoryId=" + item.id + "&name=" + encodeURIComponent(label)
      );
    return `/bbs/list?categoryId=${item.id}&name=${encodeURIComponent(label)}`;
  };

  // 转换接口数据为菜单格式
  const transformBbsData = (
    data: any,
    sectionRootId: number | null = null,
  ): NavMenuItem[] => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: any) => {
      const currentSectionRootId =
        sectionRootId ||
        ([SERVICE_GUIDE_CATEGORY_ID, NOTICE_CENTER_CATEGORY_ID].includes(
          Number(item.id),
        )
          ? Number(item.id)
          : null);
      return {
        key: `bbs_${item.id}`,
        label: getDynamicMenuLabel(item),
        path: getDynamicMenuPath(item, currentSectionRootId),
        children: transformBbsData(item.children, currentSectionRootId),
      };
    });
  };
  const menuCacheKey = `chinaone:navigation-menu:${String(locale.value).toLowerCase()}`;
  const readMenuCache = (): NavMenuItem[] => {
    try {
      const cached = JSON.parse(localStorage.getItem(menuCacheKey) || "null");
      if (
        cached?.version !== NAV_MENU_CACHE_VERSION ||
        !Array.isArray(cached?.menus)
      ) {
        localStorage.removeItem(menuCacheKey);
        return [];
      }
      return cached.menus;
    } catch {
      localStorage.removeItem(menuCacheKey);
      return [];
    }
  };
  const writeMenuCache = (menus: NavMenuItem[]) => {
    try {
      localStorage.setItem(
        menuCacheKey,
        JSON.stringify({
          version: NAV_MENU_CACHE_VERSION,
          savedAt: Date.now(),
          menus,
        }),
      );
    } catch {
      // 存储空间不可用时继续使用内存菜单，不影响导航。
    }
  };
  const createFallbackDynamicMenus = (): NavMenuItem[] => [
    {
      key: `bbs_${SERVICE_GUIDE_CATEGORY_ID}`,
      label: String(locale.value).toLowerCase().startsWith("ko")
        ? "이용 가이드"
        : t("menu.guide"),
      path: "/guide",
      children: [],
    },
    {
      key: `bbs_${NOTICE_CENTER_CATEGORY_ID}`,
      label: t("menu.customer_service"),
      path: "/notice-center?categoryId=26&page=1&search=",
      children: [],
    },
  ];

  let initialized = false;
  let dynamicMenuCache: NavMenuItem[] = readMenuCache();
  if (!dynamicMenuCache.length) dynamicMenuCache = createFallbackDynamicMenus();

  const composeNavMenus = () => {
    const guideMenus = dynamicMenuCache.filter(
      (menu) => menu.key === `bbs_${SERVICE_GUIDE_CATEGORY_ID}`,
    );
    const otherDynamicMenus = dynamicMenuCache.filter(
      (menu) => menu.key !== `bbs_${SERVICE_GUIDE_CATEGORY_ID}`,
    );
    const personalMenus = staticMenus.value.filter(
      (menu) => menu.key === "mypage",
    );
    const otherStaticMenus = staticMenus.value.filter(
      (menu) => menu.key !== "mypage",
    );
    return [
      ...guideMenus,
      ...otherStaticMenus,
      ...otherDynamicMenus,
      ...personalMenus,
    ];
  };

  // 缓存存在时首屏直接渲染完整菜单，避免接口返回后菜单位置跳动。
  const navList = ref<NavMenuItem[]>(composeNavMenus());

  // 登录状态变化时重新组装菜单
  watch(isLoggedIn, () => {
    navList.value = composeNavMenus();
  });

  // 获取动态菜单
  const fetchDynamicMenus = async () => {
    if (initialized) return;
    initialized = true;
    try {
      const dynamicMenus = await bbsService.getCategoryTree();
      dynamicMenuCache = transformBbsData(dynamicMenus);
      const serviceGuideMenu = dynamicMenuCache.find(
        (menu) => menu.key === `bbs_${SERVICE_GUIDE_CATEGORY_ID}`,
      );
      if (serviceGuideMenu) {
        serviceGuideMenu.path = "/guide";
        const setGuidePaths = (items: NavMenuItem[] = []) => {
          items.forEach((item) => {
            const categoryId = item.key?.replace("bbs_", "");
            if (categoryId)
              item.path = `/guide?categoryId=${categoryId}&page=1&search=`;
            setGuidePaths(item.children || []);
          });
        };
        setGuidePaths(serviceGuideMenu.children || []);

        // 使用指南没有子分类时，使用文章作为悬停下拉项，避免出现空菜单。
        if (!serviceGuideMenu.children?.length) {
          const guideContent = await bbsService.getContentList({
            categoryId: SERVICE_GUIDE_CATEGORY_ID,
            currentPage: 1,
            pageSize: 100,
          });
          serviceGuideMenu.children = (guideContent?.records || []).map(
            (item: any) => ({
              key: `guide_content_${item.id}`,
              label: item.title || "",
              path: `/guide/${item.id}`,
              children: [],
            }),
          );
        }
      }

      const nextNavList = composeNavMenus();
      if (JSON.stringify(navList.value) !== JSON.stringify(nextNavList)) {
        navList.value = nextNavList;
      }
      writeMenuCache(dynamicMenuCache);
    } catch (error) {
      console.error("获取菜单数据失败", error);
    }
  };

  // 初始化时获取动态菜单
  fetchDynamicMenus();

  // PC 端计算属性
  const currentMenuChildren = computed(() => {
    const menu = navList.value.find((m) => m.key === activeMenuPc.value);
    return menu?.children || [];
  });

  const currentThirdItems = computed(() => {
    const menu = navList.value.find((m) => m.key === activeMenuPc.value);
    if (!menu) return [];
    const second = menu.children?.find((s) => s.key === activeSecond.value);
    return second?.children || [];
  });

  const hasAnyTitle = computed(() => {
    return currentThirdItems.value.some((item) => item.type === "title");
  });

  // 移动端计算属性
  const currentMobileMenu = computed(() => {
    return navList.value.find((m) => m.key === activeMenu.value);
  });

  // PC 端事件处理
  const clearLeaveTimer = () => {
    if (leaveTimer !== null) {
      clearTimeout(leaveTimer);
      leaveTimer = null;
    }
  };

  const closePcMenu = () => {
    clearLeaveTimer();
    isInDropdown.value = false;
    activeMenuPc.value = null;
  };

  const handleNavEnter = (menuKey: string) => {
    clearLeaveTimer();
    isInDropdown.value = false;
    activeMenuPc.value = menuKey;
    const menu = navList.value.find((m) => m.key === menuKey);
    if (menu?.children?.length) {
      activeSecond.value = menu.children[0].key || null;
    }
  };

  const handleNavLeave = () => {
    clearLeaveTimer();
    leaveTimer = setTimeout(() => {
      leaveTimer = null;
      if (!isInDropdown.value) {
        closePcMenu();
      }
    }, 200);
  };

  const handleDropdownEnter = () => {
    isInDropdown.value = true;
    clearLeaveTimer();
  };

  const handleDropdownLeave = () => {
    closePcMenu();
  };

  onMounted(() => window.addEventListener("blur", closePcMenu));
  onBeforeUnmount(() => {
    window.removeEventListener("blur", closePcMenu);
    clearLeaveTimer();
  });

  // 获取标题下的子项
  const getItemsUnderTitle = (titleIndex: number): NavMenuItem[] => {
    const items: NavMenuItem[] = [];
    for (let i = titleIndex + 1; i < currentThirdItems.value.length; i++) {
      if (currentThirdItems.value[i].type === "title") break;
      items.push(currentThirdItems.value[i]);
    }
    return items;
  };

  // 移动端事件处理
  const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
    menuLevel.value = 1;
  };

  const openSubMenu = (menuKey: string) => {
    activeMenu.value = menuKey;
    menuLevel.value = 2;
  };

  const backToMain = () => {
    menuLevel.value = 1;
  };

  return {
    // 数据
    navList,
    // PC 端状态
    activeMenuPc,
    activeSecond,
    // PC 端计算属性
    currentMenuChildren,
    currentThirdItems,
    hasAnyTitle,
    // PC 端方法
    handleNavEnter,
    handleNavLeave,
    handleDropdownEnter,
    handleDropdownLeave,
    closePcMenu,
    getItemsUnderTitle,
    // 移动端状态
    isMobileMenuOpen,
    menuLevel,
    activeMenu,
    // 移动端计算属性
    currentMobileMenu,
    // 移动端方法
    toggleMobileMenu,
    openSubMenu,
    backToMain,
  };
}
